// Admin dashboard — bundled confirmation dialogs.
// Pure structural extraction from AdminDashboard.jsx (~275 lines total
// across 5 dialogs). Zero behaviour/UI change: every data-testid,
// class name, wording, button label, backdrop-click behaviour, and
// keyboard behaviour is preserved verbatim. State + business logic
// still lives in AdminDashboard — this file only owns presentation.
//
// Dialogs included:
//   • Delete confirmation      → move active row to Recycle Bin
//   • Permanent purge          → delete a single row from Recycle Bin forever
//   • Empty Recycle Bin        → typed "EMPTY" gate + purge all
//   • Logout confirmation      → prevents one-tap sign-outs
//   • WA Business acknowledgment → shown before EVERY WhatsApp action
//                                  until the staff member confirms once
//                                  per browser (see WA_BIZ_ACK_KEY).
//
// If you need to add a NEW confirmation dialog, add its own component
// here — do NOT let AdminDashboard.jsx regrow this block inline.

import React from "react";
import {
  Trash2, AlertTriangle, LogOut, Loader2, MessageCircle, CheckCircle2,
} from "lucide-react";

export default function ConfirmDialogs({
  // Delete (Recycle-Bin move)
  confirmDelete, setConfirmDelete, deleteSubmission,
  // Permanent purge (single row)
  confirmPurge, setConfirmPurge, purgeBusy, permanentlyDelete,
  // Empty Recycle Bin
  confirmEmpty, setConfirmEmpty,
  emptyConfirmText, setEmptyConfirmText,
  emptyRecycleBin, trashRows,
  // Logout
  confirmLogout, setConfirmLogout, logoutBusy, logout,
  // WhatsApp business-only acknowledgement
  waBizGate, setWaBizGate, confirmWaBizGate,
}) {
  return (
    <>
      {/* Delete confirmation dialog */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
          onClick={() => setConfirmDelete(null)}
          data-testid="confirm-delete-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-maroon/10 p-2.5 text-maroon flex-shrink-0">
                <Trash2 size={18} strokeWidth={1.7} />
              </div>
              <div>
                <p className="overline text-[10px]">Move to Recycle Bin</p>
                <h3 className="font-serif text-xl text-ink mt-1">
                  Delete submission for Invoice {confirmDelete.invoice_number}?
                </h3>
                <p className="text-sm text-muted2 mt-2 leading-relaxed">
                  Customer: <span className="text-ink font-medium">{confirmDelete.customer_name}</span>
                </p>
                <p className="text-sm text-muted2 mt-3 leading-relaxed">
                  The submission will move to the Recycle Bin. The invoice number
                  <span className="text-ink font-medium"> {confirmDelete.invoice_number} </span>
                  will become available for a fresh submission. Records stay in
                  the Recycle Bin until an admin permanently removes them.
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                data-testid="confirm-delete-cancel"
                onClick={() => setConfirmDelete(null)}
                className="btn-outline-maroon !px-4 !py-2 !text-xs"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-delete-confirm"
                onClick={() => deleteSubmission(confirmDelete)}
                className="btn-maroon !px-4 !py-2 !text-xs inline-flex items-center gap-1.5"
              >
                <Trash2 size={13} strokeWidth={1.7} /> Move to Recycle Bin
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Per-invoice Permanent Delete confirmation */}
      {confirmPurge && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
          onClick={() => !purgeBusy && setConfirmPurge(null)}
          data-testid="confirm-purge-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-[#F1DCDC] p-2.5 text-[#8A2A2A] flex-shrink-0">
                <AlertTriangle size={18} strokeWidth={1.7} />
              </div>
              <div className="min-w-0">
                <p className="overline text-[10px] text-[#8A2A2A]">Permanent action</p>
                <h3 className="font-serif text-xl text-ink mt-1 break-words">
                  Permanently Delete Invoice {confirmPurge.invoice_number}?
                </h3>
                <p className="text-sm text-muted2 mt-2 leading-relaxed">
                  This will <span className="text-ink font-medium">permanently remove</span>{" "}
                  this invoice and its stored submission data.{" "}
                  <span className="text-ink">It cannot be restored.</span>
                </p>
                {Array.isArray(confirmPurge.linked_invoices) && confirmPurge.linked_invoices.length > 0 && (
                  <p className="text-[11px] text-muted2 mt-3 leading-relaxed">
                    Cross-group link{confirmPurge.linked_invoices.length === 1 ? "" : "s"} to{" "}
                    <span className="text-ink font-medium">
                      {confirmPurge.linked_invoices.map((l) => l.invoice_number).join(", ")}
                    </span>{" "}
                    will be cleaned up automatically. Those invoices are not deleted.
                  </p>
                )}
              </div>
            </div>
            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                data-testid="confirm-purge-cancel"
                onClick={() => setConfirmPurge(null)}
                disabled={purgeBusy}
                className="btn-outline-maroon !px-4 !py-2 !text-xs disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-purge-confirm"
                onClick={() => permanentlyDelete(confirmPurge)}
                disabled={purgeBusy}
                className="!px-4 !py-2 !text-xs inline-flex items-center justify-center gap-1.5 rounded-full bg-[#8A2A2A] text-ivory font-medium hover:bg-[#6A1F2B] transition-colors disabled:opacity-60"
              >
                {purgeBusy ? <Loader2 className="animate-spin" size={13} /> : <Trash2 size={13} strokeWidth={1.7} />}
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty Recycle Bin confirmation */}
      {confirmEmpty && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
          onClick={() => { setConfirmEmpty(false); setEmptyConfirmText(""); }}
          data-testid="confirm-empty-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-[#F1DCDC] p-2.5 text-[#8A2A2A] flex-shrink-0">
                <AlertTriangle size={18} strokeWidth={1.7} />
              </div>
              <div>
                <p className="overline text-[10px] text-[#8A2A2A]">Permanent action</p>
                <h3 className="font-serif text-xl text-ink mt-1">Empty the Recycle Bin?</h3>
                <p className="text-sm text-muted2 mt-2 leading-relaxed">
                  This will <span className="text-ink font-medium">permanently delete</span> all
                  {" "}{trashRows.length} recycled submission{trashRows.length === 1 ? "" : "s"} and
                  their internal notes. This action cannot be undone.
                </p>
                <p className="text-xs text-muted2 mt-3 leading-relaxed">
                  Type <span className="font-mono text-ink">EMPTY</span> to confirm.
                </p>
                <input
                  data-testid="confirm-empty-input"
                  className="luxury-input !py-2 mt-2"
                  value={emptyConfirmText}
                  onChange={(e) => setEmptyConfirmText(e.target.value)}
                  placeholder="Type EMPTY to confirm"
                  autoFocus
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                data-testid="confirm-empty-cancel"
                onClick={() => { setConfirmEmpty(false); setEmptyConfirmText(""); }}
                className="btn-outline-maroon !px-4 !py-2 !text-xs"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-empty-confirm"
                onClick={emptyRecycleBin}
                disabled={emptyConfirmText.trim() !== "EMPTY"}
                className="btn-maroon !px-4 !py-2 !text-xs inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Trash2 size={13} strokeWidth={1.7} /> Permanently Delete All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logout confirmation — prevents one-tap sign-outs. Backdrop click
          simply closes the dialog (does NOT log out). Existing logout
          behaviour is preserved untouched inside the Log Out button. */}
      {confirmLogout && (
        <div
          data-testid="confirm-logout-modal"
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
          onClick={() => { if (!logoutBusy) setConfirmLogout(false); }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-logout-title"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-maroon/10 p-2.5 text-maroon flex-shrink-0">
                <LogOut size={18} strokeWidth={1.7} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="overline text-[10px]">Confirm sign out</p>
                <h3
                  id="confirm-logout-title"
                  className="font-serif text-xl text-ink mt-1 leading-tight"
                >
                  Are you sure you want to log out?
                </h3>
                <p className="text-sm text-muted2 mt-3 leading-relaxed">
                  You will need to sign in again to access the Tanish Textiles admin panel.
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                data-testid="confirm-logout-cancel"
                onClick={() => setConfirmLogout(false)}
                disabled={logoutBusy}
                className="btn-outline-maroon !px-4 !py-2 !text-xs disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-logout-confirm"
                onClick={logout}
                disabled={logoutBusy}
                className="btn-maroon !px-4 !py-2 !text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {logoutBusy ? (
                  <>
                    <Loader2 size={13} strokeWidth={1.9} className="animate-spin" /> Signing out…
                  </>
                ) : (
                  <>
                    <LogOut size={13} strokeWidth={1.9} /> Log Out
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Business-only WhatsApp acknowledgment (first run per browser).
          Every WhatsApp customer-contact action is funnelled through here
          so the staff member is explicitly reminded to use the Tanish
          Textiles Business account and never their personal WhatsApp. */}
      {waBizGate && (
        <div
          data-testid="wa-business-ack-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          onClick={() => setWaBizGate(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5 sm:p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-[#25D366]/10 p-2.5 text-[#128C7E] flex-shrink-0">
                <MessageCircle size={18} strokeWidth={1.9} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="overline text-[10px]">Send from Tanish Textiles Business only</p>
                <h3 className="font-serif text-xl text-ink mt-1 leading-tight">
                  Use WhatsApp Business — never personal WhatsApp
                </h3>
                <div className="text-sm text-muted2 mt-3 space-y-2 leading-relaxed">
                  <p>
                    Every customer message from this dashboard must be sent from
                    the <span className="text-ink font-medium">Tanish Textiles WhatsApp Business</span> account.
                    Please never send from your personal WhatsApp.
                  </p>
                  <ul className="text-[13px] pl-4 list-disc space-y-1.5">
                    <li><span className="text-ink font-medium">Desktop:</span> make sure WhatsApp Business is the account linked to WhatsApp Web on this computer.</li>
                    <li><span className="text-ink font-medium">Mobile / tablet:</span> when your phone shows the WhatsApp chooser, always tap <span className="text-ink font-medium">WhatsApp Business</span> — never plain WhatsApp.</li>
                  </ul>
                  <p className="text-[12px] text-[#8A2A2A]">
                    The website does not verify which account you finally used — the responsibility is on you at the moment WhatsApp opens.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                data-testid="wa-biz-cancel"
                onClick={() => setWaBizGate(null)}
                className="btn-outline-maroon !px-4 !py-2 !text-xs"
              >
                Cancel
              </button>
              <button
                data-testid="wa-biz-confirm"
                onClick={confirmWaBizGate}
                className="btn-maroon !px-4 !py-2 !text-xs inline-flex items-center justify-center gap-1.5"
              >
                <CheckCircle2 size={13} strokeWidth={1.9} /> I'll use Business — Open WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
